import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const ServicesPage: React.FC = () => {
  useEffect(() => {
    // SEO meta tags
    document.title = 'Koçluk Hizmetleri | Ankara Öğrenci Ebeveyn Kariyer Koçluğu';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Ankara\'da öğrenci koçluğu, ebeveyn koçluğu ve kariyer koçluğu hizmetleri. ICF standartlarında profesyonel koçluk, bilimsel yaklaşım, kişiselleştirilmiş destek.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Ankara öğrenci koçluğu, ebeveyn koçluğu hizmetleri, kariyer koçluğu Ankara, profesyonel koçluk');
    }

    // Schema.org JSON-LD for services page
    const schemaData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Profesyonel Koçluk Hizmetleri",
      "description": "Ankara'da ICF standartlarında öğrenci, ebeveyn ve kariyer koçluğu hizmetleri",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/hizmetler`,
      "provider": {
        "@type": "Person",
        "name": "Esin Arifoğlu",
        "jobTitle": "ICF Akredite Koç"
      },
      "areaServed": {
        "@type": "City",
        "name": "Ankara"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Koçluk Hizmet Kataloğu",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Öğrenci Koçluğu",
              "description": "Akademik başarı ve kişisel gelişim için öğrenci koçluğu hizmetleri",
              "serviceType": "Öğrenci Koçluğu"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Ebeveyn Koçluğu",
              "description": "Etkili ebeveynlik ve aile dinamikleri için koçluk hizmetleri",
              "serviceType": "Ebeveyn Koçluğu"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Kariyer Koçluğu",
              "description": "Profesyonel gelişim ve kariyer planlama koçluğu hizmetleri",
              "serviceType": "Kariyer Koçluğu"
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      document.title = 'Ankara Öğrenci Ebeveyn Kariyer Koçluğu | Esin Arifoğlu ICF Koçu';
    };
  }, []);

  const services = [
    {
      id: 'ogrenci-koclugu',
      icon: 'ri-book-open-line',
      title: 'Öğrenci Koçluğu',
      subtitle: 'Akademik Başarı ve Kişisel Gelişim',
      description: 'Öğrencilerin akademik hedeflerine ulaşmaları, motivasyonlarını artırmaları ve etkili çalışma teknikleri geliştirmeleri için kapsamlı destek.',
      image: 'https://public.readdy.ai/ai/img_res/df12a7df9006267372ca9d227ca1c8ce.jpg',
      features: [
        'Akademik hedef belirleme ve planlama',
        'Etkili çalışma teknikleri geliştirme',
        'Zaman yönetimi ve organizasyon becerileri',
        'Sınav kaygısı yönetimi',
        'Motivasyon artırma stratejileri',
        'Öz güven geliştirme',
        'Konsantrasyon ve odaklanma teknikleri',
        'Stres yönetimi'
      ],
      targetAudience: [
        'Lise öğrencileri',
        'Üniversite öğrencileri',
        'Sınava hazırlanan öğrenciler',
        'Akademik performansını artırmak isteyenler'
      ],
      process: [
        'İhtiyaç analizi ve hedef belirleme',
        'Kişiselleştirilmiş çalışma planı oluşturma',
        'Haftalık koçluk seansları',
        'İlerleme takibi ve değerlendirme'
      ]
    },
    {
      id: 'ebeveyn-koclugu',
      icon: 'ri-parent-line',
      title: 'Ebeveyn Koçluğu',
      subtitle: 'Etkili Ebeveynlik ve Aile Dinamikleri',
      description: 'Ebeveynlerin çocuklarıyla daha etkili iletişim kurmaları, aile dinamiklerini güçlendirmeleri ve eğitim süreçlerinde rehberlik almaları için destek.',
      image: 'https://readdy.ai/api/search-image?query=Parent%20coaching%20session%20with%20family%20focus%2C%20warm%20family%20environment%2C%20professional%20coaching%20setting%2C%20soft%20blue%20lighting%2C%20communication%20and%20understanding%20concept%2C%20modern%20home%20office%2C%20supportive%20atmosphere&width=600&height=400&seq=parent-service&orientation=landscape',
      features: [
        'Etkili ebeveyn-çocuk iletişimi',
        'Çocuk gelişimi rehberliği',
        'Aile dinamiklerini güçlendirme',
        'Eğitim süreç yönetimi',
        'Davranış geliştirme stratejileri',
        'Sınır koyma teknikleri',
        'Çatışma çözüm becerileri',
        'Ebeveyn öz bakımı'
      ],
      targetAudience: [
        'Okul çağı çocuk ebeveynleri',
        'Ergen ebeveynleri',
        'Eğitim sürecinde zorluk yaşayan aileler',
        'Aile dinamiklerini geliştirmek isteyenler'
      ],
      process: [
        'Aile dinamikleri değerlendirmesi',
        'İletişim paternlerinin analizi',
        'Kişiselleştirilmiş ebeveynlik stratejileri',
        'Düzenli takip ve destek'
      ]
    },
    {
      id: 'kariyer-koclugu',
      icon: 'ri-briefcase-line',
      title: 'Kariyer Koçluğu',
      subtitle: 'Profesyonel Gelişim ve Kariyer Planlama',
      description: 'Bireylerin kariyer hedeflerini netleştirmeleri, meslek seçimi yapmaları ve iş hayatında başarılı olmaları için kapsamlı rehberlik.',
      image: 'https://readdy.ai/api/search-image?query=Career%20coaching%20session%20in%20professional%20office%20setting%2C%20business%20professional%20with%20coach%2C%20modern%20workspace%20with%20purple%20accents%2C%20career%20development%20and%20growth%20concept%2C%20clean%20corporate%20environment&width=600&height=400&seq=career-service&orientation=landscape',
      features: [
        'Kariyer hedefleri belirleme',
        'Meslek seçimi rehberliği',
        'İş değişikliği desteği',
        'Liderlik becerileri geliştirme',
        'Kişisel marka oluşturma',
        'Networking stratejileri',
        'İş-yaşam dengesi',
        'Profesyonel gelişim planlaması'
      ],
      targetAudience: [
        'Kariyer değişimi düşünenler',
        'Yeni mezunlar',
        'Liderlik pozisyonuna hazırlananlar',
        'Profesyonel gelişim isteyenler'
      ],
      process: [
        'Kariyer değerlendirmesi ve analiz',
        'Güçlü yönler ve gelişim alanları tespiti',
        'Kariyer yol haritası oluşturma',
        'Eylem planı ve takip'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-teal-50 to-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Koçluk <span className="text-teal-600">Hizmetlerim</span>
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              ICF standartlarına uygun, bilimsel yaklaşımla desteklenen koçluk hizmetleri ile 
              yaşamınızın her alanında hedeflerinize ulaşmanızı destekliyorum.
            </p>
            <Button href="/ucretsiz-gorusme" size="lg">
              Ücretsiz Ön Görüşme
            </Button>
          </div>
        </section>

        {/* Services Detail */}
        {services.map((service, index) => (
          <section key={service.id} className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4">
                      <i className={`${service.icon} text-teal-600 text-2xl`}></i>
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                      <p className="text-lg text-teal-600">{service.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Hizmet Kapsamı:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <i className="ri-check-line text-teal-600 text-sm"></i>
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button href="/ucretsiz-gorusme" className="mb-4">
                    Bu Hizmet İçin Randevu Al
                  </Button>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img
                    src={service.image}
                    alt={`${service.title} - Profesyonel koçluk hizmetleri`}
                    className="rounded-2xl shadow-lg w-full h-80 object-cover object-top"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Hedef Kitle</h4>
                  <ul className="space-y-2">
                    {service.targetAudience.map((audience, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <i className="ri-user-line text-teal-600 text-sm"></i>
                        <span className="text-gray-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-white rounded-xl p-6 shadow-lg">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Süreç</h4>
                  <ol className="space-y-2">
                    {service.process.map((step, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <span className="w-6 h-6 bg-teal-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <span className="text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Coaching Process */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-teal-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-6">Koçluk Sürecim</h2>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                ICF standartlarına uygun, sistematik ve kişiselleştirilmiş koçluk metodolojisi
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-check-line text-3xl text-teal-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">1. Ücretsiz Ön Görüşme</h3>
                <p className="text-gray-300">
                  40 dakikalık tanışma seansında ihtiyaçlarınızı anlıyor, 
                  koçluk sürecini tanıtıyorum.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-target-line text-3xl text-blue-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">2. Hedef Belirleme</h3>
                <p className="text-gray-300">
                  SMART hedefler belirliyor, başarı kriterlerini 
                  ve ölçüm yöntemlerini tanımlıyoruz.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-chat-3-line text-3xl text-purple-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">3. Koçluk Seansları</h3>
                <p className="text-gray-300">
                  Düzenli seanslarla ilerlemenizi takip ediyor, 
                  engelleri aşmanızı destekliyorum.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-3xl text-green-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">4. Başarı ve Değerlendirme</h3>
                <p className="text-gray-300">
                  Hedeflerinize ulaştığınızda süreci değerlendiriyor, 
                  gelecek planlarınızı belirliyoruz.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Hangi Alanda Destek Almak İstiyorsunuz?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              Size en uygun koçluk hizmetini belirlemek için ücretsiz ön görüşme yapabiliriz.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button href="/ucretsiz-gorusme" size="lg">
                Ücretsiz Ön Görüşme
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
              >
                AI Asistan ile Sohbet Et
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;